import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { transparentize } from 'polished';
import { fontBody, fontTitle } from 'Styles/fonts';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import breakpoint from 'Styles/breakpoints';
import Icon from '../Icons/Icon';

export const Wrapper = styled.div`
  position: relative;
`;

/**
 * `TableContainer` is used to wrap a `Table` with constrained overflow and vertical margin.
 */
export const TableContainer = styled.div`
  overflow: scroll visible;
  border-radius: 5px;
  /* Fixes iOS Safari container overflow not clipping */
  transform: translate3d(0, 0, 0);
`;

/**
 * `Table` renders a `<table>` element.
 */
export const Table = styled.table`
  width: 100%;
`;

/**
 * `TableHead` renders a `<thead>` element.
 */
export const TableHead = styled.thead``;

/**
 * `TableBody` renders a `<tbody>` element.
 */
export const TableBody = styled.tbody``;

/**
 * `TableRow` renders a `<tr>` element, where a first-child `TableHeader` has limited
 * width and sticky positioning (overflow content is allowed to scroll horizontally behind it).
 */
export const TableRow = styled.tr`
  th:first-child {
    position: sticky;
    left: 0;
    width: 112px;
    min-width: 112px;
  }

  ${breakpoint('large')} {
    th:first-child {
      width: 144px;
      min-width: 144px;
    }
  }
`;

/**
 * `TableCell` renders a `<td>` element.
 */
export const TableCell = styled.td`
  ${fontBody(14)};
  height: 72px;
  min-width: 80px;
  text-align: center;

  vertical-align: middle;
  padding: 8px;

  /* This change allows a TableCell to appear as TableHeader since Contentful's RichText-powered tables only supports <th> within <thead>. */
  tr:not(:has(th)) &:first-of-type {
    ${fontTitle(16)};
    text-align: left;
    padding-left: 12px;
  }

  ${switchProp('$background', {
    blue: css`
      background: var(--primary);
      color: var(--background);
    `,
    grey: css`
      background: var(--hover);
      color: var(--foreground);
    `,
    white: css`
      background: var(--background);
      color: var(--foreground);
    `
  })}

  ${breakpoint('medium')} {
    ${fontBody(16)};
  }
`;
TableCell.defaultProps = {
  $background: 'white'
};
TableCell.propTypes = {
  /**
   * Cell background color
   */
  $background: PropTypes.oneOf(['blue', 'white', 'grey'])
};

/**
 * `TableHeader` renders a TableCell as a `<th>` with altered styles.
 */
export const TableHeader = styled(TableCell).attrs({ as: 'th' })`
  ${fontTitle(16)};

  &:first-of-type {
    text-align: left;
  }
`;
TableHeader.propTypes = TableCell.propTypes;

export const SwipeIcon = styled(Icon).attrs({ name: 'swipe', size: 36 })`
  position: absolute;
  padding: 14px 14px;
  border-radius: 32px;
  box-shadow: 0 0 4px 0
    ${transparentize(0.65, convertColorToThemeHexCode('black'))};
  background: var(--background);
  line-height: 36px;
  /* 32px is half the total width/height of the SwipeIcon element (including padding) */
  ${({ containerHeight }) =>
    css`
      top: ${containerHeight / 2 - 32}px;
    `}
  ${({ containerWidth }) =>
    css`
      left: ${containerWidth / 2 - 32}px;
    `}

  &.fade-enter,
  &.fade-appear {
    opacity: 0;
  }
  &.fade-enter-active,
  &.fade-appear-active {
    opacity: 1;
    transition: opacity ${({ transitionDuration }) => transitionDuration}ms
      cubic-bezier(0.25, 1, 0.25, 1);
  }
  &.fade-exit {
    opacity: 1;
  }
  &.fade-exit-active {
    opacity: 0;
    transition: opacity ${({ transitionDuration }) => transitionDuration}ms
      cubic-bezier(0.25, 1, 0.25, 1);
  }
`;
